import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {shareReplay} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';

import {BaseMenuItem} from '@models/sidebar/response/base-menu-item';
import {UserMenuItem} from '@models/sidebar/response/user-menu-item';

import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {NodeRequest} from '@models/sidebar/request/node-request';
import {PageItem} from '@models/sidebar/page-item';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {PAGE_CONTAINER_TYPE} from '@constants/sidebar/page-type.constant';
import {MenuItem} from '@models/sidebar/menu-item';
import {MESSAGE_TYPE} from '@constants/message-type';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {NotificationMessage} from '@models/sidebar/notification-message';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {
  FavoriteNotificationService
} from '@services/left-menu/favorites/favorite-notification/favorite-notification.service';
import {btou} from '@prototypes/global.functions';

@Injectable({
  providedIn: 'root'
})
export class LeftMenuService {

  private ROOT_MENU_URL_SUFFIX = '/api/web/base';
  private MENU_ITEMS_URL_SUFFIX = '/api/web/base/:parentId/:isParentMachineGroup';
  private CHANGE_BASE_MENU_ITEMS_ORDER_URL_SUFFIX = '/api/web/base/updateIndices/:isParentMachineGroup';
  private CHANGE_USER_MENU_ITEMS_ORDER_URL_SUFFIX = '/api/web/base/:machineId/userMenu/updateIndices/:parentId/:isParentMachine';
  private USER_MENU_ITEMS_URL_SUFFIX = '/api/web/base/:machineId/userMenu/:isParentMachine';
  private CREATE_NODE_URL_SUFFIX = '/api/web/base/:machineId/userMenu';
  private DELETE_NODE_URL_SUFFIX = '/api/web/base/:machineId/userMenu/:nodeId/:parentName/:indexToStartWith';
  private UPDATE_NODE_URL_SUFFIX = '/api/web/base/:machineId/userMenu/updateNode';
  private GET_PARENT_URL_SUFFIX = '/api/web/base/getParent/:itemId/:isItemMachine';

  private readonly BASE_URL: string;
  private leftMenuResponseCache = new Map();

  private readonly errorDeletingMenuItem: string = '';
  private readonly errorUpdatingBaseMenuItemsOrder: string = '';
  private readonly errorUpdatingUserMenuItemsOrder: string = '';
  private readonly successUpdatingUserMenuItemsOrder: string = '';
  private readonly successUpdatingBaseMenuItemsOrder: string = '';
  private readonly successDeletingMenuItem: string = '';
  private readonly httpStatusCodeOk: number = 200;

  private errorUpdatePageChangeBehaviourSubject = new BehaviorSubject({});
  errorUpdatePageChange = this.errorUpdatePageChangeBehaviourSubject.asObservable();


  constructor(
      private http: HttpClient,
      private apiLocation: ApiLocationService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private translateService: TranslateService,
      private router: Router,
      private favoriteNotificationService: FavoriteNotificationService) {
    this.BASE_URL = this.apiLocation.getLocation();
    this.errorDeletingMenuItem = this.translateService.instant('Failed to delete following pages');
    this.errorUpdatingBaseMenuItemsOrder = this.translateService.instant('Failed to update shared menu items order.');
    this.errorUpdatingUserMenuItemsOrder = this.translateService.instant('Failed to update user menu items order.');
    this.successDeletingMenuItem = this.translateService.instant('Following pages have been deleted');
    this.successUpdatingUserMenuItemsOrder = this.translateService.instant('User menu items order was updated.');
    this.successUpdatingBaseMenuItemsOrder = this.translateService.instant('Shared menu items order was updated.');
  }

  private static setNotificationMessageType(success: boolean, error: boolean): string {
    if (success && error) {
      return MESSAGE_TYPE.WARNING;
    } else if (success) {
      return MESSAGE_TYPE.SUCCESS;
    } else {
      return MESSAGE_TYPE.ERROR;
    }
  }

  private static getDeleteNotification(message: string, status: string, deletedName: string): string {
    if (message !== '' && message.includes(status)) {
      return '<br>' + deletedName;
    }
    return status + ':' + '<br>' + deletedName;
  }

  public resetCache(): void {
    this.leftMenuResponseCache = new Map();
  }

  public getRootMenu(): Observable<any> {
    const url: string = this.BASE_URL + this.ROOT_MENU_URL_SUFFIX;
    const rootMenuFromCache = this.leftMenuResponseCache.get(url);
    if (rootMenuFromCache) {
      return of(rootMenuFromCache);
    }

    const response = this.http.get<BaseMenuItem>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    response.subscribe((result) => {
      this.leftMenuResponseCache.set(url, result);
    });
    return response;
  }

  public getMenuItems(parentId: string, isParentMachineGroup: boolean): Observable<any> {
    let url: string = this.BASE_URL + this.MENU_ITEMS_URL_SUFFIX;
    url = url.replace(':parentId', parentId)
    .replace(':isParentMachineGroup', String(isParentMachineGroup));

    const menuItemsFromCache = this.leftMenuResponseCache.get(url);
    if (menuItemsFromCache) {
      return of(menuItemsFromCache);
    }

    const response = this.http.get<BaseMenuItem[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    response.subscribe((result) => {
      this.leftMenuResponseCache.set(url, result);
    });
    return response;
  }

  public changeBaseMenuItemsOrder(isParentMachineGroup: boolean, items: PageItem[]): Observable<any> {
    const request: PageItem[] = Object.assign([], items);
    for (const pageItem of request) {
      delete pageItem.url;
    }

    let url: string = this.BASE_URL + this.CHANGE_BASE_MENU_ITEMS_ORDER_URL_SUFFIX;
    url = url.replace(':isParentMachineGroup', String(isParentMachineGroup));
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public changeUserMenuItemsOrder(machineId: string, parentId: string, isParentMachine: boolean, items: PageItem[]): Observable<any> {
    for (const pageItem of items) {
      delete pageItem.disabled;
    }

    let url: string = this.BASE_URL + this.CHANGE_USER_MENU_ITEMS_ORDER_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':parentId', parentId)
    .replace(':isParentMachine', String(isParentMachine));
    return this.http.post(url, items, HTTPS_REQUEST_OPTIONS);
  }

  public getUserMenuItems(machineId: string, isParentMachine: boolean): Observable<any> {
    let url: string = this.BASE_URL + this.USER_MENU_ITEMS_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':isParentMachine', String(isParentMachine));

    return this.http.get<UserMenuItem[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
  }

  public createNode(machineId: string, nodeRequest: NodeRequest): Observable<any> {
    let url: string = this.BASE_URL + this.CREATE_NODE_URL_SUFFIX;
    url = url.replace(':machineId', machineId);
    return this.http.post(url, nodeRequest, HTTPS_REQUEST_OPTIONS);
  }

  public deleteNode(machineId: string, nodeId: string, machineName: string, index: number): Observable<any> {
    let url: string = this.BASE_URL + this.DELETE_NODE_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':nodeId', nodeId)
    .replace(':parentName', machineName)
    .replace(':indexToStartWith', String(index));
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public updateNode(machineId: string, userSpecificMenuItem: UserMenuItem): Observable<any> {
    let url: string = this.BASE_URL + this.UPDATE_NODE_URL_SUFFIX;
    url = url.replace(':machineId', machineId);
    return this.http.post<UserMenuItem>(url, userSpecificMenuItem, HTTPS_REQUEST_OPTIONS);
  }

  public getParent(itemId: string, isItemMachine: boolean): Observable<any> {
    let url: string = this.BASE_URL + this.GET_PARENT_URL_SUFFIX;
    url = url.replace(':itemId', itemId)
    .replace(':isItemMachine', String(isItemMachine));

    const parentFromCache = this.leftMenuResponseCache.get(url);
    if (parentFromCache) {
      return of(parentFromCache);
    }

    const response = this.http.get(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    response.subscribe((result) => {
      this.leftMenuResponseCache.set(url, result);
    });
    return response;
  }

  public getHighestPageIdNumber(pageItems: PageItem[], newItem: PageItem): number {
    let highestId: number = 0;
    for (const pageItem of pageItems) {
      if (pageItem.type !== newItem.type) {
        continue;
      }
      const pageId = pageItem.pageId;
      let id: number = 0;

      if (!pageId) {
        continue;
      }
      //take only the highest ID from the pages of the page type
      switch (pageItem.type) {
        case MenuItemType.BUCKET_BROWSER_PAGE: {
          id = parseInt(pageId.substring(PAGE_CONTAINER_TYPE.BUCKET_BROWSER_PAGE.length));
          break;
        }
        case MenuItemType.EXTERNAL_URL: {
          id = parseInt(pageId.substring(PAGE_CONTAINER_TYPE.EXTERNAL_URL_PAGE.length));
          break;
        }
        case MenuItemType.FRAME_PAGE: {
          id = parseInt(pageId.substring(PAGE_CONTAINER_TYPE.FRAME_PAGE.length));
          break;
        }
        case MenuItemType.TABLEAU_PAGE: {
          id = parseInt(pageId.substring(PAGE_CONTAINER_TYPE.TABLEAU_PAGE.length));
          break;
        }
        case MenuItemType.STATUS_EVENT_PAGE: {
          id = parseInt(pageId.substring(PAGE_CONTAINER_TYPE.STATUS_PAGE.length));
          break;
        }
      }
      highestId = id > highestId ? id : highestId;
    }
    return highestId + 1;
  }

  public checkIfElementExistsByName(valueFromInputField: string, pageItems: PageItem[], tabValue?: string): boolean {
    if (valueFromInputField.toLocaleLowerCase().trim() === tabValue?.toLocaleLowerCase()) {
      return false;
    }
    return pageItems.some((element: any) => {
      return element.name.toLowerCase() === valueFromInputField.toLocaleLowerCase().trim();
    });
  }

  public getParentId(menuItem: MenuItem): string | null {
    if (menuItem.type === MenuItemType.MACHINE) {
      return menuItem.machineId;
    }
    return menuItem.id;
  }

  public getPageItem(userMenuItem: UserMenuItem, canBeAltered: boolean): PageItem {
    const pageItem = new PageItem();
    pageItem.id = userMenuItem.id;
    pageItem.name = userMenuItem.name;
    pageItem.machineId = userMenuItem.machineId;
    pageItem.pageId = userMenuItem.pageId;
    pageItem.index = userMenuItem.index;
    pageItem.parentId = userMenuItem.parentId;
    pageItem.type = userMenuItem.type;
    pageItem.firebaseUserId = userMenuItem.firebaseUserId;
    pageItem.machineCategory = userMenuItem.machineCategory;
    pageItem.canBeAltered = canBeAltered;
    pageItem.parent = userMenuItem.parent;
    return pageItem;
  }

  public getMachineOrGroupId(menuItem: MenuItem) {
    let machineOrGroupId = '';
    if (menuItem.type === MenuItemType.MACHINE && menuItem.machineId) {
      machineOrGroupId = menuItem.machineId;
    } else if (menuItem.type === MenuItemType.MACHINE_GROUP && menuItem.id) {
      machineOrGroupId = String(menuItem.id);
    }
    return machineOrGroupId;
  }

  public getUserMenuItem(pageItem: PageItem, shared: boolean): UserMenuItem {
    const userMenuItem = new UserMenuItem();
    userMenuItem.id = pageItem.id;
    userMenuItem.name = pageItem.name;
    if (pageItem.pageId) {
      userMenuItem.pageId = pageItem.pageId;
    }
    userMenuItem.machineId = pageItem.machineId;
    userMenuItem.firebaseUserId = pageItem.firebaseUserId ? pageItem.firebaseUserId : '';
    userMenuItem.type = pageItem.type;
    userMenuItem.index = pageItem.index;
    userMenuItem.machineCategory = pageItem.machineCategory;
    userMenuItem.shared = shared;
    userMenuItem.parent = pageItem.parent;
    delete userMenuItem.parentId;
    return userMenuItem;
  }

  public getUnshareRequest(pageItem: PageItem): BaseMenuItem {
    const request = new BaseMenuItem();
    request.id = pageItem.id;
    request.name = pageItem.name;
    request.pageId = pageItem.pageId;
    request.organizationId = pageItem.organizationId ? pageItem.organizationId : null;
    request.machineId = pageItem.machineId;
    request.machineGroupId = pageItem.machineGroupId ? pageItem.machineGroupId : null;
    request.type = pageItem.type;
    request.index = pageItem.index;
    request.machineCategory = pageItem.machineCategory;
    request.parent = pageItem.parent;
    delete request.parentId;
    return request;
  }

  public getPageItems(pageItems: PageItem[]): PageItem[] {
    const newPageItems: PageItem[] = [];
    let index = 0;
    for (const pageItem of pageItems) {
      const userMenuItem = new PageItem();
      userMenuItem.id = pageItem.id;
      userMenuItem.name = pageItem.name;
      userMenuItem.machineId = pageItem.machineId;
      userMenuItem.pageId = pageItem.pageId;
      userMenuItem.firebaseUserId = pageItem.firebaseUserId;
      userMenuItem.index = index;
      userMenuItem.type = pageItem.type;
      userMenuItem.machineCategory = pageItem.machineCategory;
      userMenuItem.parent = pageItem.parent;
      delete userMenuItem.parentId;
      delete userMenuItem.organizationId;
      delete userMenuItem.machineGroupId;
      delete userMenuItem.canBeAltered;
      delete userMenuItem.formIsActive;
      newPageItems.push(userMenuItem);
      index++;
    }
    return newPageItems;
  }

  public getResultMessage(menuNotifications: MenuNotification[], results: any[]): NotificationMessage {
    let message = '';
    let success = false;
    let error = false;

    const pendingFavoriteNotifications = [...this.favoriteNotificationService.getPendingFavoriteNotifications()];
    this.favoriteNotificationService.clearPendingFavoriteNotifications();

    let pendingFavoriteNotificationsIndex = 0;
    for (const {index, result} of results.map((result, index) => ({index, result}))) {
      menuNotifications[index].result = result;
      if (menuNotifications[index].operation === OperationType.DELETE_PAGE) {
        pendingFavoriteNotifications[pendingFavoriteNotificationsIndex].result = result;
        pendingFavoriteNotificationsIndex++;
      }
    }
    this.broadcastPendingFavoriteNotifications(pendingFavoriteNotifications);

    menuNotifications = this.tableHeaderSortableService.sortItems<MenuNotification>(menuNotifications, 'operation', 'asc');

    for (const {index, menuNotification} of menuNotifications.map((menuNotification, index) => ({
      index,
      menuNotification
    }))) {
      if (this.isError(menuNotification)) {
        const __ret = this.getErrorMessage(menuNotifications, index, error, message);
        error = __ret.error;
        message = __ret.message;
      } else {
        const __ret = this.getSuccessMessage(menuNotifications, index, success, message);
        success = __ret.success;
        message = __ret.message;
      }
    }
    const notificationMessage = new NotificationMessage();
    notificationMessage.message = message;
    notificationMessage.type = LeftMenuService.setNotificationMessageType(success, error);
    return notificationMessage;
  }

  public getCommands(menuItem: MenuItem | PageItem, firebaseUserId: string, interactiveDataAnalysisMenuItem: MenuItem | undefined): any[] | undefined {
    if (!menuItem) {
      return undefined;
    }

    const parameters = {
      type: menuItem.type,
      parentType: menuItem.menuItemParent?.type,
      machine: this.getMachineId(menuItem),
      machineGroupId: this.getMachineryGroupId(menuItem),
      id: menuItem.id,
      pageId: menuItem.pageId,
      organization: menuItem.organizationId,
      name: menuItem.name,
      machineCategory: menuItem.machineCategory,
      menuItemParent: menuItem.menuItemParent,
      url: menuItem.url,
      firebaseUserId: firebaseUserId,
      actualMenuItem: menuItem,
      interactiveDataAnalysisMenuItem: interactiveDataAnalysisMenuItem,
    };

    if (menuItem.type !== MenuItemType.USER_DEFINED_PAGE && menuItem.type !== MenuItemType.USER_SHARED_PAGE
        && menuItem.type !== MenuItemType.TEMPLATE_USER_DEFINED_PAGE) {
      parameters.interactiveDataAnalysisMenuItem = undefined;
    }

    const redirectParameters = btou(JSON.stringify(parameters));

    switch (menuItem.type) {
      case MenuItemType.LOOKER_PAGE:
      case MenuItemType.WEBGL_PAGE:
        return ['page', redirectParameters];
      case MenuItemType.BUCKET_BROWSER_PAGE:
        return ['bucketBrowser', redirectParameters];
      case MenuItemType.TABLEAU_PAGE:
        return ['tableauPage', redirectParameters];
      case MenuItemType.STATUS_EVENT_PAGE:
        return ['statusEventPage', redirectParameters];
      case MenuItemType.TEMPLATE_STATUS_EVENT_PAGE:
        return ['templateStatusEventPage', redirectParameters];
      case MenuItemType.MACHINE_GROUP_STATUS_PAGE:
        return ['statusEventGroupPage', redirectParameters];
      case MenuItemType.FRAME_PAGE:
        return ['framePage', redirectParameters];
      case MenuItemType.DATA_VISUALIZATION_PAGE:
        return ['interactiveDataAnalysis', redirectParameters];
      case MenuItemType.USER_DEFINED_PAGE:
      case MenuItemType.USER_SHARED_PAGE:
        return ['userDefinedPage', redirectParameters];
      case MenuItemType.TEMPLATE_USER_DEFINED_PAGE:
        return ['templateUserDefinedPage', redirectParameters];
      case MenuItemType.FORM_PAGE:
        return ['formPage', redirectParameters];
      case MenuItemType.EXTERNAL_URL:
        if (menuItem.url && !menuItem.url.isNullOrWhitespace()) {
          window.open(menuItem.url, '_blank');
        }
        return [];
      case MenuItemType.MACHINE_GROUP:
        return ['dashboard', {id: menuItem.id, machineGroupName: menuItem.name}];
      default:
        return undefined;
    }
  }

  navigateToPage(commands: any[] | undefined): void {
    if (commands) {
      this.router.routeReuseStrategy.shouldReuseRoute = function () {
        return false;
      };
      this.router.navigate(commands);
    }
  }

  broadcastErrorUpdatePageChange(oldValues: Object): void {
    this.errorUpdatePageChangeBehaviourSubject.next(oldValues);
  }

  private broadcastPendingFavoriteNotifications(pendingFavoriteNotifications: MenuNotification[]): void {
    for (const favoriteNotification of pendingFavoriteNotifications) {
      if (!this.isError(favoriteNotification)) {
        this.favoriteNotificationService.broadcast(favoriteNotification);
      }
    }
  }

  private isError(menuNotification: MenuNotification): boolean {
    return menuNotification.result !== null && menuNotification.result !== undefined &&
        (String(menuNotification.result).startsWith('Error Status:') || menuNotification.result.status !== this.httpStatusCodeOk)
        && (Array.isArray(menuNotification.result) ? !menuNotification.result?.length : true);
  }

  private getMachineId(menuItem: MenuItem | PageItem): string {
    if (menuItem.machineId) {
      return menuItem.machineId;
    }
    if (!menuItem?.menuItemParent?.id) {
      return '';
    }
    if (menuItem.menuItemParent.type !== MenuItemType.NODE) {
      return menuItem.menuItemParent.id.split('_')[1];
    }
    if (!menuItem.menuItemParent?.menuItemParent?.id) {
      return '';
    }
    return menuItem.menuItemParent.menuItemParent.id.split('_')[1];
  }

  private getMachineryGroupId(menuItem: MenuItem | PageItem): string {
    if (!menuItem) {
      return '';
    }

    if (menuItem.machineGroupId) {
      return String(menuItem.machineGroupId);
    }

    if (!menuItem?.menuItemParent?.id) {
      return '';
    }

    return String(menuItem.menuItemParent.id);

  }

  private getSuccessMessage(menuNotifications: MenuNotification[], index: number, success: boolean, message: string) {
    const successMessage = this.getSuccessMessageByOperation(menuNotifications[index], message);
    if (successMessage !== '') {
      success = true;
      if (!message.includes(successMessage)) {
        if (message !== '' && !message.endsWith('<br>') && !successMessage.startsWith('<br>')) {
          message += '<br>';
        }
        message += successMessage;
      }
    }
    return {success, message};
  }

  private getErrorMessage(menuNotifications: MenuNotification[], index: number, error: boolean, message: string) {
    const errorMessage = this.getErrorMessageByOperation(menuNotifications[index], message);
    if (errorMessage !== '') {
      error = true;
      if (!message.includes(errorMessage)) {
        if (message !== '') {
          message += '<br>';
        }
        message += errorMessage;
      }
    }
    return {error, message};
  }

  private getErrorMessageByOperation(messageDetails: MenuNotification, message: string): string {
    switch (messageDetails.operation) {
      case OperationType.DELETE_PAGE:
      case OperationType.DELETE:
        return LeftMenuService.getDeleteNotification(message, this.errorDeletingMenuItem, messageDetails.name);
      case OperationType.UPDATE_BASE_MENU_ITEMS_ORDER:
        return this.errorUpdatingBaseMenuItemsOrder + '<br>';
      case OperationType.UPDATE_USER_MENU_ITEMS_ORDER:
        return this.errorUpdatingUserMenuItemsOrder + '<br>';
      default:
        return '';
    }
  }

  private getSuccessMessageByOperation(messageDetails: MenuNotification, message: string): string {
    switch (messageDetails.operation) {
      case OperationType.DELETE_PAGE:
      case OperationType.DELETE:
        return LeftMenuService.getDeleteNotification(message, this.successDeletingMenuItem, messageDetails.name);
      case OperationType.UPDATE_BASE_MENU_ITEMS_ORDER:
        return this.successUpdatingBaseMenuItemsOrder + '<br>';
      case OperationType.UPDATE_USER_MENU_ITEMS_ORDER:
        return this.successUpdatingUserMenuItemsOrder + '<br>';
      default:
        return '';
    }
  }


}
